"use client";
// beui.dev/components/motion/tabs

import { motion, MotionConfig, useReducedMotion, useSpring, type Transition } from "motion/react";
import { useCallback, useId, useMemo, useState, type ReactNode } from "react";
import { TabsCtx, type Variant } from "./TabsContext";
import type { IconDisplay } from "./TabsContext";
export { TabsList } from "./TabsList";
export { TabsTrigger, TabsContent } from "./TabsParts";

// Settle without overshoot: a scrollable tab list would turn even a small
// overshoot into a transient scrollbar and layout shift.
// Scale stiffness and damping together for a quicker glide with the same feel.
const transition: Transition = {
  type: "spring",
  stiffness: 245,
  damping: 36,
  mass: 1.2,
};

export function Tabs({
  defaultValue,
  value,
  onValueChange,
  variant = "pill",
  icons = "always",
  children,
  className,
}: {
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  variant?: Variant;
  /**
   * With "selected", a trigger's icon springs open only on the selected tab
   * and folds away when another is chosen. Defaults to "always".
   */
  icons?: IconDisplay;
  children: ReactNode;
  className?: string;
}) {
  const [internal, setInternal] = useState(defaultValue ?? "");
  const layoutId = useId();
  const reduce = useReducedMotion();
  const controlled = value !== undefined;
  const current = controlled ? value : internal;
  const setValue = useCallback(
    (v: string) => {
      if (!controlled) setInternal(v);
      onValueChange?.(v);
    },
    [controlled, onValueChange],
  );
  const x = useSpring(0, transition);
  const y = useSpring(0, transition);
  const width = useSpring(0, transition);
  const height = useSpring(0, transition);
  const track = useMemo(() => ({ x, y, width, height }), [x, y, width, height]);
  const [placed, setPlaced] = useState(false);
  const [tabs, setTabs] = useState<Map<string, boolean>>(new Map());
  const registerTab = useCallback((tab: string, disabled: boolean) => {
    setTabs((map) => (map.get(tab) === disabled ? map : new Map(map).set(tab, disabled)));
  }, []);
  const unregisterTab = useCallback((tab: string) => {
    setTabs((map) => {
      if (!map.has(tab)) return map;
      const next = new Map(map);
      next.delete(tab);
      return next;
    });
  }, []);
  const [panels, setPanels] = useState<ReadonlySet<string>>(new Set());
  const registerPanel = useCallback((panel: string) => {
    setPanels((set) => (set.has(panel) ? set : new Set(set).add(panel)));
  }, []);
  const unregisterPanel = useCallback((panel: string) => {
    setPanels((set) => {
      if (!set.has(panel)) return set;
      const next = new Set(set);
      next.delete(panel);
      return next;
    });
  }, []);
  // Resolved during render so the list has exactly one tab stop in the same
  // commit that changes the selection or the tabs.
  let tabStop: string | null = null;
  if (tabs.size > 0) {
    const enabled = Array.from(tabs).filter(([, disabled]) => !disabled);
    tabStop = enabled.some(([tab]) => tab === current) ? current : (enabled[0]?.[0] ?? "");
  }
  const baseId = useId();
  const tabId = useCallback((tab: string) => `${baseId}-tab-${tab.replace(/[^\w-]/g, "_")}`, [baseId]);
  const panelId = useCallback((tab: string) => `${baseId}-panel-${tab.replace(/[^\w-]/g, "_")}`, [baseId]);
  const contextValue = useMemo(
    () => ({
      value: current,
      setValue,
      layoutId,
      variant,
      icons,
      track,
      placed,
      setPlaced,
      tabStop,
      registerTab,
      unregisterTab,
      panels,
      registerPanel,
      unregisterPanel,
      tabId,
      panelId,
    }),
    [current, icons, layoutId, setValue, variant, track, placed, tabStop, registerTab, unregisterTab, panels, registerPanel, unregisterPanel, tabId, panelId],
  );
  return (
    <MotionConfig transition={reduce ? { duration: 0 } : transition}>
      <TabsCtx.Provider value={contextValue}>
        {/* layoutRoot: the indicator's layoutId measures in page coordinates, so
            inside fixed/scrolled containers it would replay scroll offsets as
            movement. The pill only ever travels within the list, so scoping
            projection to the Tabs wrapper is always correct. */}
        <motion.div layoutRoot className={className}>
          {children}
        </motion.div>
      </TabsCtx.Provider>
    </MotionConfig>
  );
}
